import { collection, doc, addDoc, updateDoc, getDocs, query, where, orderBy, limit, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { LMSProvider, AttendanceRecord, Student, LMSSync, LMSNotification } from '../types/lms';
import { TaskService } from './TaskService';
import { NotificationService } from './NotificationService';
import CanvasService from './CanvasService';
import InfiniteCampusService from './InfiniteCampusService';

interface InfiniteCampusAttendanceResponse {
  students: Array<{
    personID: string;
    firstName: string;
    lastName: string;
    grade: number;
    attendanceStatus: 'present' | 'absent' | 'tardy';
    lastUpdated: string;
  }>;
}

interface CanvasAttendanceResponse {
  attendance: Array<{
    user_id: string;
    status: 'present' | 'absent' | 'tardy';
    updated_at: string;
  }>;
}

export class LMSService {
  private static async getLMSProvider(schoolId: string): Promise<LMSProvider | null> {
    const q = query(
      collection(db, 'lmsProviders'),
      where('schoolId', '==', schoolId),
      where('syncEnabled', '==', true)
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() } as LMSProvider;
  }

  static async syncAttendance(schoolId: string): Promise<void> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider) {
      throw new Error('No LMS provider configured');
    }

    const syncRecord = await this.createSyncRecord(schoolId, 'attendance');
    
    try {
      await this.updateSyncStatus(syncRecord.id, 'in-progress');
      
      let attendanceData: AttendanceRecord[] = [];
      
      switch (provider.type) {
        case 'infinite-campus':
          attendanceData = await this.syncInfiniteCampusAttendance(provider);
          break;
        case 'canvas':
          attendanceData = await this.syncCanvasAttendance(provider);
          break;
        case 'powerschool':
          attendanceData = await this.syncPowerSchoolAttendance(provider);
          break;
        default:
          throw new Error(`Unsupported LMS type: ${provider.type}`);
      }

      // Process attendance records and create notifications
      await this.processAttendanceRecords(schoolId, attendanceData);
      
      await this.updateSyncStatus(syncRecord.id, 'completed', {
        recordsProcessed: attendanceData.length,
        recordsUpdated: attendanceData.length,
        warnings: []
      });

      // Update provider's last sync time
      await updateDoc(doc(db, 'lmsProviders', provider.id), {
        lastSync: serverTimestamp(),
        nextSyncAt: new Date(Date.now() + provider.syncInterval * 1000)
      });

    } catch (error) {
      console.error('Error syncing attendance:', error);
      await this.updateSyncStatus(syncRecord.id, 'failed', undefined, error.message);
      throw error;
    }
  }

  private static async syncInfiniteCampusAttendance(provider: LMSProvider): Promise<AttendanceRecord[]> {
    const icService = new InfiniteCampusService(provider);
    
    try {
      const today = new Date().toISOString().split('T')[0];
      const sections = await icService.getSections({ active: true });
      const attendanceRecords: AttendanceRecord[] = [];

      for (const section of sections) {
        try {
          // Get attendance for this section
          const attendance = await icService.getAttendance({
            startDate: today,
            endDate: today,
            sectionID: section.sectionID
          });

          // Get enrollments for this section
          const enrollments = await icService.getEnrollments({
            sectionID: section.sectionID,
            active: true
          });

          // Create attendance record for this section/period
          const record: AttendanceRecord = {
            id: `ic_${section.sectionID}_${today}_${section.period}`,
            date: today,
            period: section.period,
            teacherId: section.teacherID,
            submitted: attendance.length > 0,
            submittedAt: attendance.length > 0 ? attendance[0]?.lastUpdated || new Date().toISOString() : '',
            students: {}
          };

          // Map attendance data
          if (attendance.length > 0) {
            attendance.forEach(att => {
              record.students[att.personID] = {
                status: att.attendanceStatus,
                note: att.note || ''
              };
            });
          } else {
            // If no attendance submitted, mark all enrolled students as not recorded
            enrollments.forEach(enrollment => {
              record.students[enrollment.personID] = {
                status: 'present', // Default assumption
                note: 'Not recorded in Infinite Campus'
              };
            });
            record.submitted = false;
          }

          attendanceRecords.push(record);
        } catch (sectionError) {
          console.warn(`Error syncing attendance for section ${section.sectionID}:`, sectionError);
          // Continue with other sections
        }
      }

      return attendanceRecords;
    } catch (error) {
      console.error('Error syncing Infinite Campus attendance:', error);
      throw new Error(`Infinite Campus API error: ${error.message}`);
    }
  }

  private static async syncCanvasAttendance(provider: LMSProvider): Promise<AttendanceRecord[]> {
    const canvasService = new CanvasService(provider);
    
    try {
      // Get all active courses where user is a teacher
      const courses = await canvasService.getCourses({
        enrollment_type: 'teacher',
        state: 'available',
        include: ['enrollments']
      });

      const attendanceRecords: AttendanceRecord[] = [];
      const today = new Date().toISOString().split('T')[0];

      for (const course of courses) {
        try {
          // Get attendance for each course
          const attendance = await canvasService.getCourseAttendance(course.id, today);
          const enrollments = await canvasService.getCourseEnrollments(course.id, {
            type: ['StudentEnrollment'],
            state: ['active'],
            include: ['user']
          });

          // Get teacher enrollment
          const teacherEnrollments = await canvasService.getCourseEnrollments(course.id, {
            type: ['TeacherEnrollment'],
            state: ['active'],
            include: ['user']
          });
          const teacherId = teacherEnrollments[0]?.user?.id?.toString() || '';

          // Convert Canvas attendance to our format
          const record: AttendanceRecord = {
            id: `canvas_${course.id}_${today}`,
            date: today,
            period: 1, // Canvas doesn't have periods, so default to 1
            teacherId,
            submitted: attendance.length > 0,
            submittedAt: attendance.length > 0 ? new Date().toISOString() : '',
            students: {}
          };

          // Map attendance data
          if (attendance.length > 0) {
            attendance.forEach(att => {
              record.students[att.user_id.toString()] = {
                status: att.status,
                note: ''
              };
            });
          } else {
            // If no attendance data, mark all students as not recorded
            enrollments.forEach(enrollment => {
              if (enrollment.user) {
                record.students[enrollment.user.id.toString()] = {
                  status: 'present', // Default assumption
                  note: 'Not recorded in Canvas'
                };
              }
            });
            record.submitted = false;
          }

          attendanceRecords.push(record);
        } catch (courseError) {
          console.warn(`Error syncing attendance for course ${course.id}:`, courseError);
          // Continue with other courses
        }
      }

      return attendanceRecords;
    } catch (error) {
      console.error('Error syncing Canvas attendance:', error);
      throw new Error(`Canvas API error: ${error.message}`);
    }
  }

  private static async syncPowerSchoolAttendance(provider: LMSProvider): Promise<AttendanceRecord[]> {
    // PowerSchool integration would go here
    throw new Error('PowerSchool integration not yet implemented');
  }

  private static async processAttendanceRecords(schoolId: string, records: AttendanceRecord[]): Promise<void> {
    const missingAttendanceThreshold = 30 * 60 * 1000; // 30 minutes in milliseconds
    const now = new Date();
    
    for (const record of records) {
      // Save attendance record
      await addDoc(collection(db, 'attendanceRecords'), {
        ...record,
        schoolId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Check for missing attendance (if not submitted after 30 minutes)
      if (!record.submitted) {
        const periodStart = new Date(record.date);
        const timeSinceStart = now.getTime() - periodStart.getTime();
        
        if (timeSinceStart > missingAttendanceThreshold) {
          // Create notification for missing attendance
          await this.createLMSNotification(schoolId, {
            type: 'attendance-missing',
            title: 'Missing Attendance',
            message: `Attendance for period ${record.period} has not been submitted`,
            recipients: [record.teacherId],
            priority: 'high',
            actionRequired: true,
            actionUrl: `/attendance/${record.id}`
          });

          // Create task for teacher to submit attendance
          await TaskService.createTask({
            title: 'Submit Missing Attendance',
            description: `Please submit attendance for period ${record.period}`,
            assignedTo: record.teacherId,
            dueDate: new Date(Date.now() + 60 * 60 * 1000), // 1 hour from now
            priority: 'high',
            schoolId,
            type: 'attendance',
            status: 'pending'
          });
        }
      }

      // Check for student status changes
      for (const [studentId, attendance] of Object.entries(record.students)) {
        if (attendance.status === 'in-school-suspension' || attendance.status === 'field-trip') {
          await this.createLMSNotification(schoolId, {
            type: 'status-change',
            title: 'Student Status Update',
            message: `Student status changed to ${attendance.status}`,
            recipients: ['all-staff'], // Notify all staff
            priority: 'medium',
            actionRequired: false
          });
        }
      }
    }
  }

  private static async createSyncRecord(schoolId: string, type: LMSSync['type']): Promise<LMSSync> {
    const syncRecord = {
      schoolId,
      type,
      status: 'pending' as const,
      startedAt: new Date().toISOString(),
      syncInterval: 300 // 5 minutes
    };

    const docRef = await addDoc(collection(db, 'lmsSyncs'), {
      ...syncRecord,
      createdAt: serverTimestamp()
    });

    return { id: docRef.id, ...syncRecord };
  }

  private static async updateSyncStatus(
    syncId: string, 
    status: LMSSync['status'], 
    details?: LMSSync['details'],
    error?: string
  ): Promise<void> {
    const updateData: any = {
      status,
      updatedAt: serverTimestamp()
    };

    if (status === 'completed' || status === 'failed') {
      updateData.completedAt = new Date().toISOString();
    }

    if (details) {
      updateData.details = details;
    }

    if (error) {
      updateData.error = error;
    }

    await updateDoc(doc(db, 'lmsSyncs', syncId), updateData);
  }

  private static async createLMSNotification(schoolId: string, notification: Omit<LMSNotification, 'id' | 'createdAt' | 'read'>): Promise<void> {
    await NotificationService.createNotification({
      ...notification,
      schoolId,
      createdAt: new Date().toISOString(),
      read: false
    });
  }

  static async configureLMSProvider(schoolId: string, config: Omit<LMSProvider, 'id'>): Promise<void> {
    // Check if provider already exists
    const q = query(
      collection(db, 'lmsProviders'),
      where('schoolId', '==', schoolId)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      // Create new provider
      await addDoc(collection(db, 'lmsProviders'), {
        ...config,
        schoolId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      // Update existing provider
      const existingDoc = snapshot.docs[0];
      await updateDoc(existingDoc.ref, {
        ...config,
        updatedAt: serverTimestamp()
      });
    }
  }

  static async testLMSConnection(provider: LMSProvider): Promise<boolean> {
    try {
      switch (provider.type) {
        case 'infinite-campus':
          const icService = new InfiniteCampusService(provider);
          return await icService.testConnection();
          
        case 'canvas':
          const canvasService = new CanvasService(provider);
          return await canvasService.testConnection();
          
        case 'powerschool':
          const psResponse = await fetch(`${provider.apiUrl}/ws/v1/district/info`, {
            headers: {
              'Authorization': `Bearer ${provider.apiKey}`,
              'Content-Type': 'application/json'
            }
          });
          return psResponse.ok;
          
        default:
          throw new Error(`Unsupported LMS type: ${provider.type}`);
      }
    } catch (error) {
      console.error('LMS connection test failed:', error);
      return false;
    }
  }

  static async getSyncHistory(schoolId: string, type?: LMSSync['type']): Promise<LMSSync[]> {
    let q = query(
      collection(db, 'lmsSyncs'),
      where('schoolId', '==', schoolId),
      orderBy('startedAt', 'desc'),
      limit(50)
    );

    if (type) {
      q = query(
        collection(db, 'lmsSyncs'),
        where('schoolId', '==', schoolId),
        where('type', '==', type),
        orderBy('startedAt', 'desc'),
        limit(50)
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as LMSSync[];
  }

  static watchSyncStatus(schoolId: string, callback: (syncs: LMSSync[]) => void): () => void {
    const q = query(
      collection(db, 'lmsSyncs'),
      where('schoolId', '==', schoolId),
      where('status', 'in', ['pending', 'in-progress']),
      orderBy('startedAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const syncs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LMSSync[];
      callback(syncs);
    });
  }

  static async scheduleSync(schoolId: string, type: LMSSync['type'], intervalSeconds: number): Promise<void> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider) {
      throw new Error('No LMS provider configured');
    }

    // Update sync interval
    await updateDoc(doc(db, 'lmsProviders', provider.id), {
      syncInterval: intervalSeconds,
      nextSyncAt: new Date(Date.now() + intervalSeconds * 1000),
      updatedAt: serverTimestamp()
    });

    // In a real implementation, you'd schedule this with a job queue or cron job
    // For now, we'll just update the database
  }

  // Canvas-specific methods
  static async syncCanvasCourses(schoolId: string): Promise<void> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'canvas') {
      throw new Error('Canvas provider not configured');
    }

    const canvasService = new CanvasService(provider);
    const courses = await canvasService.getCourses({
      enrollment_type: 'teacher',
      state: 'available',
      include: ['enrollments']
    });

    // Save courses to database
    for (const course of courses) {
      await addDoc(collection(db, 'courses'), {
        schoolId,
        lmsId: course.id.toString(),
        lmsType: 'canvas',
        name: course.name,
        courseCode: course.course_code,
        startDate: course.start_at,
        endDate: course.end_at,
        totalStudents: course.total_students,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  }

  static async syncCanvasGrades(schoolId: string, courseId: number): Promise<void> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'canvas') {
      throw new Error('Canvas provider not configured');
    }

    const canvasService = new CanvasService(provider);
    const [assignments, grades] = await Promise.all([
      canvasService.getCourseAssignments(courseId),
      canvasService.getCourseGrades(courseId)
    ]);

    // Save assignments and grades
    for (const assignment of assignments) {
      await addDoc(collection(db, 'assignments'), {
        schoolId,
        lmsId: assignment.id.toString(),
        lmsType: 'canvas',
        courseId: courseId.toString(),
        name: assignment.name,
        description: assignment.description,
        dueDate: assignment.due_at,
        pointsPossible: assignment.points_possible,
        gradingType: assignment.grading_type,
        isPublished: assignment.workflow_state === 'published',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }

    for (const grade of grades) {
      await addDoc(collection(db, 'grades'), {
        schoolId,
        lmsType: 'canvas',
        assignmentId: grade.assignment_id.toString(),
        studentId: grade.user_id.toString(),
        currentScore: grade.current_score,
        finalScore: grade.final_score,
        currentGrade: grade.current_grade,
        finalGrade: grade.final_grade,
        workflowState: grade.workflow_state,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  }

  static async getCanvasAnalytics(schoolId: string, courseId: number): Promise<any> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'canvas') {
      throw new Error('Canvas provider not configured');
    }

    const canvasService = new CanvasService(provider);
    return await canvasService.getCourseAnalytics(courseId);
  }

  static async sendCanvasMessage(schoolId: string, courseId: number, message: {
    recipients: number[];
    subject: string;
    body: string;
  }): Promise<boolean> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'canvas') {
      throw new Error('Canvas provider not configured');
    }

    try {
      const canvasService = new CanvasService(provider);
      await canvasService.sendMessage(courseId, message);
      return true;
    } catch (error) {
      console.error('Error sending Canvas message:', error);
      return false;
    }
  }

  static async createCanvasAssignment(schoolId: string, courseId: number, assignment: {
    name: string;
    description?: string;
    due_at?: string;
    points_possible?: number;
    grading_type?: string;
    submission_types?: string[];
    published?: boolean;
  }): Promise<boolean> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'canvas') {
      throw new Error('Canvas provider not configured');
    }

    try {
      const canvasService = new CanvasService(provider);
      const createdAssignment = await canvasService.createAssignment(courseId, assignment);
      
      // Save to local database
      await addDoc(collection(db, 'assignments'), {
        schoolId,
        lmsId: createdAssignment.id.toString(),
        lmsType: 'canvas',
        courseId: courseId.toString(),
        name: createdAssignment.name,
        description: createdAssignment.description,
        dueDate: createdAssignment.due_at,
        pointsPossible: createdAssignment.points_possible,
        gradingType: createdAssignment.grading_type,
        isPublished: createdAssignment.workflow_state === 'published',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Error creating Canvas assignment:', error);
      return false;
    }
  }

  static async submitCanvasAttendance(schoolId: string, courseId: number, attendanceData: {
    user_id: number;
    status: 'present' | 'absent' | 'tardy' | 'excused';
    date: string;
  }[]): Promise<boolean> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'canvas') {
      throw new Error('Canvas provider not configured');
    }

    try {
      const canvasService = new CanvasService(provider);
      const success = await canvasService.submitAttendance(courseId, attendanceData);
      
      if (success) {
        // Update local attendance records
        const attendanceRecord = {
          id: `canvas_${courseId}_${attendanceData[0]?.date}`,
          date: attendanceData[0]?.date || new Date().toISOString().split('T')[0],
          period: 1,
          teacherId: '', // Will be filled from provider info
          submitted: true,
          submittedAt: new Date().toISOString(),
          students: attendanceData.reduce((acc, att) => {
            acc[att.user_id.toString()] = {
              status: att.status,
              note: ''
            };
            return acc;
          }, {} as Record<string, { status: string; note: string }>)
        };

        await addDoc(collection(db, 'attendanceRecords'), {
          ...attendanceRecord,
          schoolId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      return success;
    } catch (error) {
      console.error('Error submitting Canvas attendance:', error);
      return false;
    }
  }

  // Infinite Campus specific methods
  static async syncInfiniteCampusStudents(schoolId: string): Promise<void> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'infinite-campus') {
      throw new Error('Infinite Campus provider not configured');
    }

    const icService = new InfiniteCampusService(provider);
    const { students, sections, enrollments, teachers } = await icService.syncAllStudentData();

    // Save students to database
    for (const student of students) {
      await addDoc(collection(db, 'students'), {
        schoolId,
        lmsId: student.personID,
        lmsType: 'infinite-campus',
        firstName: student.firstName,
        lastName: student.lastName,
        middleName: student.middleName,
        grade: student.grade,
        studentNumber: student.studentNumber,
        email: student.email,
        phone: student.phone,
        address: student.address,
        emergencyContacts: student.emergencyContacts,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }

    // Save sections
    for (const section of sections) {
      await addDoc(collection(db, 'sections'), {
        schoolId,
        lmsId: section.sectionID,
        lmsType: 'infinite-campus',
        courseName: section.courseName,
        courseCode: section.courseCode,
        roomNumber: section.roomNumber,
        period: section.period,
        teacherId: section.teacherID,
        teacherName: section.teacherName,
        enrolledStudents: section.enrolledStudents,
        startDate: section.startDate,
        endDate: section.endDate,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }

    // Save teachers
    for (const teacher of teachers) {
      await addDoc(collection(db, 'teachers'), {
        schoolId,
        lmsId: teacher.staffID,
        lmsType: 'infinite-campus',
        firstName: teacher.firstName,
        lastName: teacher.lastName,
        email: teacher.email,
        phone: teacher.phone,
        department: teacher.department,
        sections: teacher.sections,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  }

  static async syncInfiniteCampusGrades(schoolId: string, sectionID: string): Promise<void> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'infinite-campus') {
      throw new Error('Infinite Campus provider not configured');
    }

    const icService = new InfiniteCampusService(provider);
    const grades = await icService.getSectionGrades(sectionID);

    // Save grades to database
    for (const grade of grades) {
      await addDoc(collection(db, 'grades'), {
        schoolId,
        lmsType: 'infinite-campus',
        sectionId: grade.sectionID,
        assignmentId: grade.assignmentID,
        assignmentName: grade.assignmentName,
        studentId: grade.personID,
        score: grade.score,
        maxPoints: grade.maxPoints,
        letterGrade: grade.letterGrade,
        dateAssigned: grade.dateAssigned,
        dateDue: grade.dateDue,
        category: grade.category,
        weight: grade.weight,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  }

  static async getInfiniteCampusAttendanceAlerts(schoolId: string, threshold = 90): Promise<any[]> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'infinite-campus') {
      throw new Error('Infinite Campus provider not configured');
    }

    const icService = new InfiniteCampusService(provider);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return await icService.getAttendanceAlerts({
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      threshold
    });
  }

  static async submitInfiniteCampusAttendance(schoolId: string, attendanceData: Array<{
    personID: string;
    sectionID: string;
    date: string;
    period: number;
    attendanceCode: string;
    minutes?: number;
    note?: string;
  }>): Promise<boolean> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'infinite-campus') {
      throw new Error('Infinite Campus provider not configured');
    }

    try {
      const icService = new InfiniteCampusService(provider);
      const success = await icService.submitAttendance(attendanceData);
      
      if (success) {
        // Update local attendance records
        for (const att of attendanceData) {
          const attendanceRecord = {
            id: `ic_${att.sectionID}_${att.date}_${att.period}`,
            date: att.date,
            period: att.period,
            teacherId: '', // Will be filled from section info
            submitted: true,
            submittedAt: new Date().toISOString(),
            students: {
              [att.personID]: {
                status: att.attendanceCode,
                note: att.note || ''
              }
            }
          };

          await addDoc(collection(db, 'attendanceRecords'), {
            ...attendanceRecord,
            schoolId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      }

      return success;
    } catch (error) {
      console.error('Error submitting Infinite Campus attendance:', error);
      return false;
    }
  }

  static async submitInfiniteCampusGrades(schoolId: string, sectionID: string, grades: Array<{
    personID: string;
    assignmentID: string;
    score: number;
    letterGrade?: string;
    comment?: string;
  }>): Promise<boolean> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'infinite-campus') {
      throw new Error('Infinite Campus provider not configured');
    }

    try {
      const icService = new InfiniteCampusService(provider);
      const success = await icService.submitGrades(sectionID, grades);
      
      if (success) {
        // Update local grade records
        for (const grade of grades) {
          await addDoc(collection(db, 'grades'), {
            schoolId,
            lmsType: 'infinite-campus',
            sectionId: sectionID,
            assignmentId: grade.assignmentID,
            studentId: grade.personID,
            score: grade.score,
            letterGrade: grade.letterGrade,
            comment: grade.comment,
            submittedAt: new Date().toISOString(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      }

      return success;
    } catch (error) {
      console.error('Error submitting Infinite Campus grades:', error);
      return false;
    }
  }

  static async getInfiniteCampusAttendanceSummary(schoolId: string, startDate: string, endDate: string, sectionID?: string): Promise<any> {
    const provider = await this.getLMSProvider(schoolId);
    if (!provider || provider.type !== 'infinite-campus') {
      throw new Error('Infinite Campus provider not configured');
    }

    const icService = new InfiniteCampusService(provider);
    return await icService.getAttendanceSummary({
      startDate,
      endDate,
      sectionID
    });
  }
}

// Task Service interface - this would be implemented in TaskService.ts
class TaskService {
  static async createTask(task: any): Promise<void> {
    await addDoc(collection(db, 'tasks'), {
      ...task,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }
}